import camelcaseKeys from 'camelcase-keys';
import type { Options } from 'camelcase-keys';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { getDomain } from '@libs/utils';

export function apiUrl(): string {
  if (getDomain.isProduction) {
    return getRelativePath();
  }

  if (getDomain.isLocalhost && isFeatureToggleActive('local-server')) {
    return '/';
  }

  return 'https://test.sembo.se/master';
}

export function camelCased<T extends object>(
  options: Options
): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>) =>
    // NOTE: camelcase-keys from version 9.x.x uses more narrow types.
    // For input:
    // T extends Record<string, unknown> | ReadonlyArray<Record<string, unknown>>
    // It is incompatible with concrete types we use causing:
    // "Index signature for type 'string' is missing in type Foo" errors.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source.pipe(map((input) => camelcaseKeys(input as any, options)));
}

export function getRelativePath(): string {
  if (!(getDomain.isWhiteLabel || getDomain.isStenaLine)) {
    return '/d/';
  }

  return '/';
}

export function buildErrorMessage(messages: string[]): string {
  return messages.filter((message) => message).join('. ');
}

export async function getResult<R>(response: Response): Promise<R> {
  const contentType = response.headers.get('content-type');
  try {
    if (!contentType) {
      const value = await response.text();
      return value as R;
    } else {
      if (contentType.includes('application/json')) {
        return await response.json();
      } else {
        return (await response.text()) as R;
      }
    }
  } catch {
    throw new Error(
      buildErrorMessage([
        '[REST API Error]: Content type not supported.',
        `Content type: ${contentType}`,
        `Endpoint: ${response.url}`,
      ])
    );
  }
}
